import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, Image as ImageIcon, Tag, Layers, Save, Send, Upload, Trash2, X, Plus, Camera, Eye, EyeOff } from 'lucide-react';
import { settingsApi } from '../apiExtras';
import { useBranding } from '../context/BrandingContext';
import BrandLogo from '../components/BrandLogo';
import { toast } from 'sonner';

const Section = ({ icon: Icon, title, desc, children }) => (
  <section className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8">
    <div className="flex items-start gap-3 mb-6">
      <div className="w-10 h-10 rounded-xl bg-[#C9A961]/15 text-[#C9A961] grid place-items-center shrink-0"><Icon className="w-5 h-5" /></div>
      <div>
        <h2 className="font-display text-2xl">{title}</h2>
        {desc && <p className="text-sm text-slate-400 mt-1">{desc}</p>}
      </div>
    </div>
    {children}
  </section>
);

const AdminSettings = () => {
  const { refresh } = useBranding();
  const [loading, setLoading] = useState(true);
  const [smtp, setSmtp] = useState({ enabled: false, host: 'smtp.gmail.com', port: 587, user: '', password: '', from_name: 'FRAMEBOX by Nicola Verardo', from_email: '', use_ssl: false, admin_notify_email: '' });
  const [logo, setLogo] = useState({ logo_url: '', show_text: true, hero_video_url: '' });
  const [watermark, setWatermark] = useState({ enabled: true, text: 'FRAMEBOX', opacity: 0.15 });
  const [categories, setCategories] = useState([]);
  const [newCat, setNewCat] = useState('');
  const [testTo, setTestTo] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [busy, setBusy] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const s = await settingsApi.getAll();
        if (s.smtp && Object.keys(s.smtp).length) setSmtp({ ...smtp, ...s.smtp, password: '' });
        if (s.logo) setLogo(s.logo);
        if (s.watermark) setWatermark(s.watermark);
        if (s.categories) setCategories(s.categories);
      } catch (err) { toast.error('Errore caricamento impostazioni'); }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveSmtp = async () => {
    setBusy(b => ({ ...b, smtp: true }));
    try {
      const payload = { ...smtp };
      if (!payload.password) delete payload.password; // keep existing
      await settingsApi.updateSmtp(payload);
      toast.success('SMTP salvato');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Errore salvataggio'); }
    finally { setBusy(b => ({ ...b, smtp: false })); }
  };

  const doTestSmtp = async () => {
    if (!testTo.trim()) return toast.error('Inserisci un indirizzo email destinatario');
    setBusy(b => ({ ...b, test: true }));
    try {
      await settingsApi.testSmtp(testTo.trim());
      toast.success(`Email di test inviata a ${testTo}`);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Test fallito'); }
    finally { setBusy(b => ({ ...b, test: false })); }
  };

  const onLogoFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) return toast.error('Deve essere un\'immagine');
    if (f.size > 2 * 1024 * 1024) return toast.error('Max 2MB');
    const r = new FileReader();
    r.onload = () => setLogo(l => ({ ...l, logo_url: r.result }));
    r.readAsDataURL(f);
  };

  const saveLogo = async () => {
    setBusy(b => ({ ...b, logo: true }));
    try { await settingsApi.updateLogo(logo); await refresh(); toast.success('Logo aggiornato'); }
    catch { toast.error('Errore salvataggio'); }
    finally { setBusy(b => ({ ...b, logo: false })); }
  };

  const clearLogo = async () => {
    if (!window.confirm('Rimuovere il logo? Torneremo all\'icona di default.')) return;
    setLogo({ ...logo, logo_url: '' });
    try { await settingsApi.updateLogo({ ...logo, logo_url: '' }); await refresh(); toast.success('Logo rimosso'); }
    catch { toast.error('Errore'); }
  };

  const saveWatermark = async () => {
    setBusy(b => ({ ...b, wm: true }));
    try { await settingsApi.updateWatermark(watermark); await refresh(); toast.success('Watermark aggiornato'); }
    catch { toast.error('Errore'); }
    finally { setBusy(b => ({ ...b, wm: false })); }
  };

  const addCategory = () => {
    const v = newCat.trim();
    if (!v) return;
    if (categories.includes(v)) return toast.error('Categoria già presente');
    setCategories([...categories, v]);
    setNewCat('');
  };

  const removeCategory = (c) => setCategories(categories.filter(x => x !== c));

  const saveCategories = async () => {
    setBusy(b => ({ ...b, cat: true }));
    try { const r = await settingsApi.updateCategories(categories); setCategories(r.categories); toast.success('Categorie salvate'); }
    catch { toast.error('Errore'); }
    finally { setBusy(b => ({ ...b, cat: false })); }
  };

  if (loading) return <div className="min-h-screen bg-[#0B0B0D] text-white p-10">Caricamento...</div>;

  return (
    <div className="min-h-screen bg-[#0B0B0D] text-white">
      <header className="border-b border-white/10 bg-black/40 backdrop-blur sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-3 flex items-center gap-4">
          <Link to="/admin" className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-white"><ArrowLeft className="w-4 h-4" /> Dashboard</Link>
          <div className="h-4 w-px bg-white/10" />
          <BrandLogo size="sm" />
          <div className="ml-auto text-xs text-slate-400 hidden sm:block">Impostazioni</div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 md:px-6 py-10 space-y-6">
        <div>
          <p className="text-[#C9A961] uppercase tracking-[0.25em] text-xs font-semibold">Configurazione</p>
          <h1 className="font-display text-4xl md:text-5xl mt-1">Impostazioni</h1>
          <p className="text-slate-400 mt-1">Personalizza brand, categorie, watermark e configura l'invio email.</p>
        </div>

        {/* LOGO */}
        <Section icon={ImageIcon} title="Logo" desc="Sostituisci l'icona di default con il tuo logo. Consigliato PNG con sfondo trasparente.">
          <div className="grid md:grid-cols-[220px_1fr] gap-6 items-start">
            <div className="rounded-xl bg-black/40 border border-white/10 p-6 flex items-center justify-center min-h-[120px]">
              {logo.logo_url ? <img src={logo.logo_url} alt="Logo" className="max-h-24 max-w-full object-contain" /> : <Camera className="w-14 h-14 text-[#C9A961]" />}
            </div>
            <div className="space-y-3">
              <label className="flex items-center justify-center gap-2 cursor-pointer bg-white/5 border-2 border-dashed border-white/10 hover:border-[#C9A961] rounded-xl p-4 transition">
                <Upload className="w-4 h-4 text-[#C9A961]" />
                <span className="text-sm">Carica file logo (PNG/JPG/SVG, max 2MB)</span>
                <input type="file" accept="image/*" className="hidden" onChange={onLogoFile} />
              </label>
              <label className="block">
                <span className="text-xs text-slate-300">... oppure incolla URL</span>
                <input value={logo.logo_url.startsWith('data:') ? '' : logo.logo_url} onChange={e=>setLogo({...logo, logo_url:e.target.value})} placeholder="https://..." className="mt-1 w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#C9A961]" />
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={logo.show_text} onChange={e=>setLogo({...logo, show_text:e.target.checked})} className="accent-[#C9A961]" />
                Mostra scritta "FRAMEBOX by Nicola Verardo" accanto al logo
              </label>
              <label className="block pt-2">
                <span className="text-xs text-slate-300">Video animato di sfondo (Landing) — URL MP4</span>
                <input value={logo.hero_video_url || ''} onChange={e=>setLogo({...logo, hero_video_url:e.target.value})} placeholder="https://.../logo.mp4 (lascia vuoto per nessuno sfondo)" className="mt-1 w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#C9A961]" />
                <span className="block text-[11px] text-slate-500 mt-1">Consigliato: MP4 breve in loop, senza audio. Verrà mostrato in trasparenza dietro il testo della home.</span>
              </label>
              <div className="flex items-center gap-2 pt-1">
                <button onClick={saveLogo} disabled={busy.logo} className="inline-flex items-center gap-1.5 bg-[#C9A961] hover:bg-white text-black text-xs font-semibold px-4 py-2 rounded-full"><Save className="w-3.5 h-3.5" /> {busy.logo ? 'Salvataggio...' : 'Salva logo'}</button>
                {logo.logo_url && <button onClick={clearLogo} className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-rose-400 px-3 py-2"><Trash2 className="w-3.5 h-3.5" /> Rimuovi</button>}
              </div>
            </div>
          </div>
        </Section>

        {/* CATEGORIES */}
        <Section icon={Tag} title="Categorie di lavoro" desc="Personalizza l'elenco di categorie disponibili quando crei un nuovo lavoro.">
          <div className="flex flex-wrap gap-2 mb-4">
            {categories.length === 0 && <p className="text-sm text-slate-500">Nessuna categoria. Aggiungine almeno una.</p>}
            {categories.map(c => (
              <span key={c} className="inline-flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-full pl-3 pr-1 py-1 text-sm">
                {c}
                <button onClick={()=>removeCategory(c)} className="w-5 h-5 rounded-full bg-white/10 hover:bg-rose-500 hover:text-white text-slate-400 grid place-items-center"><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
          <div className="flex items-center gap-2 max-w-md">
            <input value={newCat} onChange={e=>setNewCat(e.target.value)} onKeyDown={e=>{ if (e.key==='Enter') { e.preventDefault(); addCategory(); }}} placeholder="Nuova categoria (es. Book, Maternity)" className="flex-1 bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#C9A961]" />
            <button onClick={addCategory} className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white text-xs font-semibold px-3 py-2 rounded-full"><Plus className="w-3.5 h-3.5" /> Aggiungi</button>
          </div>
          <button onClick={saveCategories} disabled={busy.cat} className="mt-4 inline-flex items-center gap-1.5 bg-[#C9A961] hover:bg-white text-black text-xs font-semibold px-4 py-2 rounded-full"><Save className="w-3.5 h-3.5" /> {busy.cat ? 'Salvataggio...' : 'Salva categorie'}</button>
        </Section>

        {/* WATERMARK */}
        <Section icon={Layers} title="Watermark anteprime" desc="Sovrapponi il tuo marchio in oro sulle anteprime foto per proteggere la tua consegna. I download singoli restano puliti.">
          <label className="flex items-center gap-2 text-sm mb-4 cursor-pointer">
            <input type="checkbox" checked={watermark.enabled} onChange={e=>setWatermark({...watermark, enabled:e.target.checked})} className="accent-[#C9A961]" />
            Mostra watermark sulle anteprime della galleria cliente
          </label>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs text-slate-300">Testo watermark</span>
              <input value={watermark.text} onChange={e=>setWatermark({...watermark, text:e.target.value})} className="mt-1 w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#C9A961]" />
            </label>
            <label className="block">
              <span className="text-xs text-slate-300">Opacità ({Math.round((watermark.opacity || 0) * 100)}%)</span>
              <input type="range" min="0.05" max="0.5" step="0.05" value={watermark.opacity} onChange={e=>setWatermark({...watermark, opacity:parseFloat(e.target.value)})} className="mt-2 w-full accent-[#C9A961]" />
            </label>
          </div>
          <button onClick={saveWatermark} disabled={busy.wm} className="mt-4 inline-flex items-center gap-1.5 bg-[#C9A961] hover:bg-white text-black text-xs font-semibold px-4 py-2 rounded-full"><Save className="w-3.5 h-3.5" /> {busy.wm ? 'Salvataggio...' : 'Salva watermark'}</button>
        </Section>

        {/* SMTP */}
        <Section icon={Mail} title="Invio email (SMTP Gmail)" desc="Configura Gmail SMTP per inviare automaticamente credenziali ai clienti e notifiche delle selezioni.">
          <div className="bg-[#F7F1E1]/10 border border-[#C9A961]/30 rounded-xl p-4 text-xs text-slate-300 mb-5">
            <b className="text-[#C9A961]">Come ottenere l'App Password Gmail:</b>
            <ol className="mt-2 space-y-1 list-decimal list-inside">
              <li>Attiva l'autenticazione a 2 fattori su <span className="font-mono">myaccount.google.com/security</span></li>
              <li>Vai su <span className="font-mono">myaccount.google.com/apppasswords</span></li>
              <li>Crea una App Password chiamata "FRAMEBOX" → copia i 16 caratteri</li>
              <li>Incollala nel campo <b>App Password</b> qui sotto e salva</li>
            </ol>
          </div>

          <label className="flex items-center gap-2 text-sm mb-4 cursor-pointer">
            <input type="checkbox" checked={smtp.enabled} onChange={e=>setSmtp({...smtp, enabled:e.target.checked})} className="accent-[#C9A961]" />
            SMTP attivo (invia email reali; se disattivo, le credenziali/notifiche restano solo in-app)
          </label>

          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block sm:col-span-2">
              <span className="text-xs text-slate-300">Email mittente (Gmail)</span>
              <input value={smtp.user} onChange={e=>setSmtp({...smtp, user:e.target.value, from_email: e.target.value})} placeholder="verardonicola@gmail.com" className="mt-1 w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#C9A961]" />
            </label>
            <label className="block sm:col-span-2">
              <span className="text-xs text-slate-300">App Password Gmail (16 caratteri)</span>
              <div className="mt-1 flex items-center gap-2 bg-black/40 border border-white/10 rounded-xl px-3 py-2 focus-within:border-[#C9A961]">
                <input type={showPass ? 'text' : 'password'} value={smtp.password} onChange={e=>setSmtp({...smtp, password:e.target.value})} placeholder="xxxx xxxx xxxx xxxx" className="flex-1 bg-transparent outline-none text-sm font-mono" />
                <button type="button" onClick={()=>setShowPass(!showPass)} className="text-slate-400 hover:text-white">{showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
              </div>
              <span className="block text-[11px] text-slate-500 mt-1">La password è salvata in modo sicuro sul server. Lascia vuoto per non modificarla.</span>
            </label>
            <label className="block">
              <span className="text-xs text-slate-300">Nome mittente visualizzato</span>
              <input value={smtp.from_name} onChange={e=>setSmtp({...smtp, from_name:e.target.value})} className="mt-1 w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#C9A961]" />
            </label>
            <label className="block">
              <span className="text-xs text-slate-300">Email per notifiche selezioni (di solito la tua)</span>
              <input value={smtp.admin_notify_email} onChange={e=>setSmtp({...smtp, admin_notify_email:e.target.value})} placeholder="verardonicola@gmail.com" className="mt-1 w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#C9A961]" />
            </label>
            <label className="block">
              <span className="text-xs text-slate-300">SMTP host</span>
              <input value={smtp.host} onChange={e=>setSmtp({...smtp, host:e.target.value})} className="mt-1 w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#C9A961] font-mono" />
            </label>
            <label className="block">
              <span className="text-xs text-slate-300">Porta ({smtp.port === 465 ? 'SSL' : 'TLS'})</span>
              <input type="number" value={smtp.port} onChange={e=>setSmtp({...smtp, port:parseInt(e.target.value)||587, use_ssl: parseInt(e.target.value)===465})} className="mt-1 w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#C9A961]" />
            </label>
          </div>

          <div className="flex items-center gap-2 mt-5 flex-wrap">
            <button onClick={saveSmtp} disabled={busy.smtp} className="inline-flex items-center gap-1.5 bg-[#C9A961] hover:bg-white text-black text-xs font-semibold px-4 py-2 rounded-full"><Save className="w-3.5 h-3.5" /> {busy.smtp ? 'Salvataggio...' : 'Salva SMTP'}</button>
            <div className="h-6 w-px bg-white/10" />
            <input value={testTo} onChange={e=>setTestTo(e.target.value)} placeholder="Invia test a..." className="bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#C9A961] max-w-[220px]" />
            <button onClick={doTestSmtp} disabled={busy.test || !smtp.enabled} className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 border border-white/10 text-xs font-semibold px-4 py-2 rounded-full disabled:opacity-50"><Send className="w-3.5 h-3.5" /> {busy.test ? 'Invio...' : 'Invia test'}</button>
          </div>
        </Section>
      </div>
    </div>
  );
};

export default AdminSettings;
